import React, { useEffect, useRef, useState, useCallback } from "react";
import configProvider from "config/configProvider";
import { Grid, Skeleton } from "@mui/material";
import vehiclesService from "services/vehiclesService";
import "mapbox-gl/dist/mapbox-gl.css";
import vanIcon from "assets/images/airport_shuttle_24dp.svg";
import { Map, Source, Layer, MapRef, Popup } from "react-map-gl";
import { clusterLayer, currentLocationLayer } from "./layers";
import type { GeoJSONSource } from "react-map-gl";
import type { FeatureCollection } from "geojson";
import MDBox from "components/MDBox";
import { bbox, lineString } from "@turf/turf";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { webglUnavailableMessage } from "../webglSupport";
import { useMapWebglRecovery } from "map/useMapWebglRecovery";

const CurrentLocation = () => {
  const mapRef = useRef<MapRef>(null);
  // Rebuilds the map if its WebGL context dies; see the hook for why.
  const { mapKey, reuseMaps } = useMapWebglRecovery(mapRef);
  const [mapCamera, setMapCamera] = useState({
    longitude: -100,
    latitude: 40,
    zoom: 3.5,
  });

  const [locations, setLocations] = useState<FeatureCollection>({ type: "FeatureCollection", features: [] });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldCluster, setShouldCluster] = useState(false);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (!searchParams.get("unitnumber"))return;

      let unitNumber = searchParams.get("unitnumber");
      vehiclesService.getVehicleLocationByUnitNumber(unitNumber).then((results) =>{
        if(!results || !results.length)return;
        let fc: FeatureCollection = {
            type: "FeatureCollection",
            features: results.map((r) => ({
              type: "Feature",
              properties: { ...r },
              geometry: { type: "Point", coordinates: [r.telemetry[0].longitude, r.telemetry[0].latitude] },
            })),
          };
        if(results.count >1) {
          const points = fc.features.map((f) => f.geometry["coordinates"]);
          const line = lineString(points);
          const [minLng, minLat, maxLng, maxLat] = bbox(line);
          mapRef.current.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            { padding: 40, duration: 1000 }
          );} else{
          setMapCamera({ zoom: 9, longitude: results[0].telemetry[0].longitude, latitude: results[0].telemetry[0].latitude });
           
          }
      
        setLocations(fc);
    }).finally(() => setLoading(false));
 
    return () => {
      setShouldCluster(true);
      setMapLoaded(false);
      mapRef.current = null;
    };
  }, []);


  const onClick = (event) => {
    const feature = event.features[0];

    if (!feature) return;

    const clusterId = feature.properties.cluster_id;
    const mapboxSource = mapRef.current.getSource("vehicles") as GeoJSONSource;

    if (clusterId) {
      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return;
        }

        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom,
          duration: 500,
        });
      });
    } else {
      let zoomCamera = mapCamera.zoom < 17 ? 17 : mapCamera.zoom;
      if (mapCamera.zoom < 20)
        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoomCamera,
          duration: 700,
        });
    }
  };

  const onLoad = (e) => {
    if (!mapRef.current) return;

    const pinImage = new Image();
    pinImage.onload = () => {
      if (!mapRef.current.hasImage("pin")) {
        mapRef.current.addImage("pin", pinImage, { sdf: true });
      }
    };
    pinImage.src = vanIcon; // pin is your svg import
    pinImage.height = 25;
    pinImage.width = 25;
    pinImage.className = "van-pin";
    setMapLoaded(true);
  };

  const onHover = useCallback((event: any) => {
    const {
      features,
      point: { x, y },
      lngLat,
    } = event;
    if (!features || features[0]?.layer?.id != "unclustered-point") return;
    const hoveredFeature = features && features[0];

    // prettier-ignore
    setHoverInfo(hoveredFeature && {feature: hoveredFeature, x: lngLat?.lat, y:lngLat?.lng});
    return () => {
      setMapLoaded(false);
      mapRef.current = null;
    };
  }, []);

  const skeleton = () => {
    return (
      <>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton variant="rectangular" height={50} />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={11}>
            {" "}
            <Skeleton variant="rectangular" height={"74vh"} />
          </Grid>
        </Grid>
      </>
    );
  };
  const parseProperty = (p) => {
    try {
      var propertyArray = JSON.parse(p);
      if (Array.isArray(propertyArray) && propertyArray.length > 0) {
        return propertyArray[0];
      }
    } catch {}
    return undefined;
  };
  const parseDate = (dateToParse) =>{
    let date = parseProperty(dateToParse);
      return date?.lastUpdateDT ? moment.utc(date.lastUpdateDT).local().format('MM/DD/YYYY HH:mm') : null;
  }
  const parseDatesForLink = (dateToParse) =>{
    let startDate = parseDate(dateToParse);
    return startDate ?  `&startDate=${moment(startDate).subtract(1, 'days').toISOString()}&endDate=${moment(startDate).toISOString()}`
      : `&startDate=${moment().subtract(1, 'days').toISOString()}&endDate=${moment().toISOString()}`;
  }
  const mapView = () => {
    return (
      <>
        <div>
          <Map
            key={mapKey}
            reuseMaps={reuseMaps}
            {...mapCamera}
            onMove={(e) => setMapCamera(e.viewState)}
            onLoad={onLoad}
            mapStyle="mapbox://styles/mapbox/standard"
            style={{ width: "75vw", height: "71vh" }}
            mapboxAccessToken={configProvider.MapboxApiKey}
            interactiveLayerIds={[clusterLayer.id, "unclustered-point"]}
            onClick={onClick}
            onMouseMove={onHover}
            failIfMajorPerformanceCaveat={false}
            onError={(e: any) => {
              if (/webgl/i.test(e?.error?.message ?? "")) setMapError(true);
            }}
            ref={mapRef}
          >
            {mapLoaded && (
              <Source id="vehicles" type="geojson" data={locations} cluster={false}>
                 <Layer {...currentLocationLayer} />
              </Source>
            )}
            {hoverInfo && (
              <Popup
                key={hoverInfo.y + hoverInfo.x}
                longitude={hoverInfo.y}
                latitude={hoverInfo.x}
                closeOnMove={true}
                closeButton={true}
                focusAfterOpen={false}
                offset={10}
              >
                <div className="row">
                  Unit #:{" "}
                  <Link
                    to={`/maps/history?unitNumber=${hoverInfo.feature.properties.unitNumber}${parseDatesForLink(hoverInfo.feature.properties.telemetry)}`}
                  >
                    {hoverInfo.feature.properties.unitNumber}
                  </Link>
                </div>
                <div className="row">Heading: {parseProperty(hoverInfo.feature.properties.telemetry)?.heading?? 'N/A'}</div>
                <div className="row">Speed: {parseProperty(hoverInfo.feature.properties.telemetry)?.speedMPH?? 'N/A'}/MPH</div>
                <div className="row">Updated: {parseDate(hoverInfo.feature.properties.telemetry) ?? 'N/A'}</div>
                <div className="row">Lat: {parseProperty(hoverInfo.feature.properties.telemetry)?.latitude?? 'N/A'}</div>
                <div className="row">Long: {parseProperty(hoverInfo.feature.properties.telemetry)?.longitude?? 'N/A'}</div>
              </Popup>
            )}
          </Map>
        </div>
      </>
    );
  };

  if (loading) return skeleton();
  if (mapError) {
    return (
      <MDBox sx={{ p: 3, textAlign: "center", color: "error.main" }}>{webglUnavailableMessage}</MDBox>
    );
  }
  return mapView();
};
export default CurrentLocation;
